import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import urlBuilder from 'garaje/utils/url-builder';

interface NotifUser {
  id: number;
  name: string;
}

interface NotifEventState {
  enabled: boolean;
  roles: string[];
  users: NotifUser[];
}

interface NotifStateMap {
  [eventId: string]: NotifEventState;
}

interface NotifRecipient {
  id: number;
  name: string;
  email: string;
}

// Shape of the route model: the backend's NotificationsView (notifications slice +
// recipient pool). Both routes return this so the controller stays cast-free.
export interface NotificationsModel {
  notifications?: NotifStateMap;
  users?: NotifRecipient[];
}

const NOTIFICATION_DEFS = [
  {
    id: 'succeeded',
    tone: 'success',
    icon: 'circle-check',
    title: 'Success',
    desc: 'Sent when the routine completes successfully.',
  },
  {
    id: 'partial',
    tone: 'warning',
    icon: 'caution-triangle',
    title: 'Partial failure',
    desc: 'Sent when some steps within the routine failed; review the audit log for details.',
  },
  {
    id: 'failed',
    tone: 'danger',
    icon: 'ui-exclamation',
    title: 'Failure',
    desc: 'Sent when all steps in the routine failed, and all re-attempts are exhausted.',
  },
];

const DEFAULT_STATE: NotifStateMap = {
  succeeded: { enabled: true, roles: ['Global admin', 'Location admin'], users: [] },
  partial: { enabled: true, roles: ['Global admin', 'Location admin'], users: [] },
  failed: { enabled: true, roles: ['Global admin', 'Location admin'], users: [] },
};

// Deep clone so notifState and savedState never share nested objects/arrays — the
// isDirty comparison (and cancel/save round-trips) depend on the two being independent.
function cloneState(state: NotifStateMap): NotifStateMap {
  return JSON.parse(JSON.stringify(state)) as NotifStateMap;
}

export default class LocationOverviewHrisNotificationsController extends Controller {
  @service declare abilities: AbilitiesService;
  @service declare ajaxFetch: AjaxFetchService;
  @service declare flashMessages: FlashMessagesService;
  @service declare state: StateService;

  declare model: NotificationsModel;

  get isGlobal(): boolean {
    return this.abilities.can('manage all hris-dashboard');
  }

  get isReadOnly(): boolean {
    return !this.isGlobal;
  }

  notificationDefs = NOTIFICATION_DEFS;

  // M1 recipient roles per the design mockup. Employee guard / Receptionist are
  // not valid recipient roles for HRIS notifications and the backend rejects them.
  roleOptions = ['Global admin', 'Location admin'];

  get userOptions(): NotifRecipient[] {
    return this.model?.users ?? [];
  }

  @tracked notifState: NotifStateMap = cloneState(DEFAULT_STATE);
  @tracked savedState: NotifStateMap = cloneState(DEFAULT_STATE);
  @tracked isSaving = false;

  get isDirty(): boolean {
    return JSON.stringify(this.notifState) !== JSON.stringify(this.savedState);
  }

  get notifCards(): Array<(typeof NOTIFICATION_DEFS)[number] & { state: NotifEventState }> {
    return this.notificationDefs.map((def) => ({
      ...def,
      state: this.notifState[def.id] ?? { enabled: false, roles: [], users: [] },
    }));
  }

  setupFromModel(): void {
    // Treat an absent OR empty notifications map as "no saved config" → show the
    // enabled defaults. The backend returns `notifications: {}` for a company that
    // hasn't saved settings; without this guard an empty object is used as-is and
    // every card renders collapsed (the recipients section is gated on `enabled`).
    const saved = this.model?.notifications;
    const initial = saved && Object.keys(saved).length > 0 ? saved : DEFAULT_STATE;
    this.notifState = cloneState(initial);
    this.savedState = cloneState(initial);
  }

  @action
  toggleEvent(eventId: string): void {
    const current = this.notifState[eventId] ?? { enabled: false, roles: [], users: [] };
    this.notifState = {
      ...this.notifState,
      [eventId]: { ...current, enabled: !current.enabled },
    };
  }

  @action
  setRoles(eventId: string, roles: string[]): void {
    const current = this.notifState[eventId] ?? { enabled: false, roles: [], users: [] };
    this.notifState = {
      ...this.notifState,
      [eventId]: { ...current, roles },
    };
  }

  @action
  setUsers(eventId: string, users: NotifUser[]): void {
    const current = this.notifState[eventId] ?? { enabled: false, roles: [], users: [] };
    this.notifState = {
      ...this.notifState,
      [eventId]: { ...current, users },
    };
  }

  @action
  async save(): Promise<void> {
    // In-flight guard: a double-click must not fire two concurrent PUTs.
    if (this.isSaving) return;
    this.isSaving = true;
    // Snapshot what we send BEFORE awaiting so a late edit can't be mistaken for "saved".
    const submitted = cloneState(this.notifState);
    try {
      const result = await this.ajaxFetch.request<NotificationsModel>(
        urlBuilder.hrisLifecycle.url(`/notifications?company_id=${this.state.currentCompany.id}`),
        {
          method: 'PUT',
          // ajaxFetch defaults contentType to form-urlencoded; the body is JSON and
          // hris-lifecycle's @RequestBody expects application/json (else it 415s).
          contentType: 'application/json; charset=utf-8',
          data: { notifications: submitted },
        },
      );
      // Seed from the server's canonical response (it may normalize roles/recipients);
      // fall back to what we submitted if the backend echoes nothing.
      const persisted = result?.notifications ?? submitted;
      this.notifState = cloneState(persisted);
      this.savedState = cloneState(persisted);
      this.flashMessages.showAndHideFlash('success', 'Notification settings saved');
    } catch (err) {
      this.flashMessages.showAndHideFlash('error', 'Failed to save notification settings');
      // eslint-disable-next-line no-console
      console.error('[notifications][save] Failed to save notification settings:', err);
    } finally {
      this.isSaving = false;
    }
  }

  @action
  cancel(): void {
    this.notifState = cloneState(this.savedState);
  }
}
